import { useEffect, useMemo, useContext } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import businessApi from '../api/business.api'
import { QueryKeys } from '../queries/querykeys'
import Tabs from '../components/modules/tabs/Tabs'
import Layout from '../components/layouts/index'
import Bancos from '../components/modules/views/dashboard/Administracion/bancos/Index'
import IncomePage from '../components/modules/views/dashboard/Administracion/Ingresos'
import EgresosPage from '../components/modules/views/dashboard/Administracion/egresos'
import VinculacionPage from '../components/modules/views/dashboard/Administracion/Vinculaciones/index'
import RemuneracionPage from '../components/modules/views/dashboard/Administracion/RRHH'
import GestionPage from '../components/modules/views/dashboard/Administracion/Gestion/index'
import TransaccionesPage from '../components/modules/views/dashboard/Administracion/pagosdigitales/table/List'
import { useAdministracionTabs, useIntegrationStatus } from './useAdministracion.hooks'
import { IntegrationsAPI } from '../api/integrations.api'
import GlobalContext from '../context/global-context'

const Administracion = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { setActiveTab } = useAdministracionTabs()
  const { currentBusiness } = useContext(GlobalContext)
  const { setStatus, setLoading, setError } = useIntegrationStatus()
  const queryClient = useQueryClient()
  const integrationsAPI = useMemo(() => new IntegrationsAPI(), [])

  useEffect(() => {
    const fetchIntegrationStatus = async () => {
      if (!currentBusiness?.id) return

      try {
        setLoading(true)
        const response = await integrationsAPI.getIntegrationsStatus(currentBusiness.id)
        setStatus(response.data)
      } catch (error) {
        console.error('Error fetching integration status:', error)
        setError(error?.response?.data?.message || 'Error al obtener estado de integraciones')
      }
    }

    fetchIntegrationStatus()
  }, [currentBusiness?.id, integrationsAPI, setStatus, setLoading, setError])

  useEffect(() => {
    if (!currentBusiness?.id) return

    const year = new Date().getFullYear()

    queryClient.prefetchQuery({
      queryKey: [QueryKeys.GET_MONTHLY_SUMMARY, currentBusiness.id, year, undefined, 'mercadopago'],
      queryFn: () =>
        businessApi
          .getMonthlySummary(currentBusiness.id, { year, provider: 'mercadopago' })
          .then(res => res.data),
      staleTime: 5 * 60 * 1000
    })
  }, [currentBusiness?.id, queryClient])

  const tabs = useMemo(
    () => [
      {
        key: 'clientes',
        title: 'Clientes',
        content: <IncomePage />
      },
      {
        key: 'proveedores',
        title: 'Proveedores',
        content: <EgresosPage />
      },
      {
        key: 'bancos',
        title: 'Bancos',
        content: <Bancos />
      },
      {
        key: 'pagos',
        title: 'Pagos Digitales',
        content: <TransaccionesPage />
      },
      {
        key: 'vinculaciones',
        title: 'Vinculaciones',
        content: <VinculacionPage />
      },
      {
        key: 'remuneraciones',
        title: 'Remuneraciones',
        content: <RemuneracionPage />
      },
      {
        key: 'gestion',
        title: 'Gestion Comercial',
        content: <GestionPage />
      }
    ],
    []
  )

  const tabParam = searchParams.get('tab')
  const activeTabIndex = tabs.findIndex(tab => tab.key === tabParam)
  const resolvedTabIndex = activeTabIndex >= 0 ? activeTabIndex : 0

  useEffect(() => {
    const currentTab = tabs[resolvedTabIndex]
    if (currentTab?.key) {
      setActiveTab(currentTab.key)
    }
  }, [resolvedTabIndex, setActiveTab, tabs])

  const handleTabClick = index => {
    const tab = tabs[index]
    if (tab?.key) {
      setSearchParams({ tab: tab.key }, { replace: true })
    }
  }

  return (
    <Layout className='px-4 md:px-8'>
      <Tabs tabs={tabs} activeIndex={resolvedTabIndex} onTabClick={handleTabClick} />
    </Layout>
  )
}

export default Administracion
